import { defineMessage } from 'react-intl'

import { isEqual } from 'lodash-es'

import {
  OPTIONS_TYPE,
  PreferenceMetadataConfig,
  RENDER_CONDITION_TARGETS,
  SHOULD_EXPAND_ACCORDION_CONDITION_TARGETS,
} from './types'
import { CountryNameToISOCode } from '../../intl/constants'
import {
  MEMBER_PREFERENCE_IDS,
  MEMBER_PREFERENCE_NAMES,
  S4C_PROGRAM_OPTION_FLOW,
} from '../../memberPreferences/constants'
import { MEMBER_PREFERENCE_LABELS } from '../../searchForCare/messages'

const optionsDefault = [
  {
    text: MEMBER_PREFERENCE_LABELS.highlightProvidersWhoOfferInPersonSessions,
    id: MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS,
    selectedTextLabel: MEMBER_PREFERENCE_LABELS.inPerson,
  },
]

const footerTextDefault = {
  message: defineMessage({
    defaultMessage: 'Ok! We will also show you providers who offer in-person',
    description: 'Description text confirming checkbox selection',
  }),
  renderConditions: [
    {
      target: RENDER_CONDITION_TARGETS.SELECTED_PREFERENCES,
      path: 'meetingSetting',
      fn: (val: any) => isEqual(val, [MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS]),
    },
  ],
  color: 'textSecondary',
}

const countryConfigDefault = {
  programOptionFlows: [S4C_PROGRAM_OPTION_FLOW.THERAPY],
  options: optionsDefault,
  footerText: footerTextDefault,
}

const meetingStylesPreferenceTherapy: PreferenceMetadataConfig = {
  name: MEMBER_PREFERENCE_NAMES.MEETING_SETTING,
  title: MEMBER_PREFERENCE_LABELS.meetingSetting,
  icon: 'MemberPreferencesMeetingSettingIllustrationIcon',
  optionType: OPTIONS_TYPE.CHECKBOX,
  promptText: defineMessage({
    defaultMessage:
      'Meeting by video gives you access to the highest number of eligible providers, as well as a wider array of specialists.',
    description:
      'Description text informing users that there will always be matches who offer video sessions in order to display a wide array of specialists',
  }),
  countryConfig: {
    [CountryNameToISOCode.Argentina]: countryConfigDefault,
    [CountryNameToISOCode.Australia]: countryConfigDefault,
    [CountryNameToISOCode.Austria]: countryConfigDefault,
    [CountryNameToISOCode.Belgium]: countryConfigDefault,
    [CountryNameToISOCode.Brazil]: countryConfigDefault,
    [CountryNameToISOCode.Bulgaria]: countryConfigDefault,
    [CountryNameToISOCode.Canada]: countryConfigDefault,
    [CountryNameToISOCode.Chile]: countryConfigDefault,
    [CountryNameToISOCode.Colombia]: countryConfigDefault,
    [CountryNameToISOCode.CostaRica]: countryConfigDefault,
    [CountryNameToISOCode.Croatia]: countryConfigDefault,
    [CountryNameToISOCode.CzechRepublic]: countryConfigDefault,
    [CountryNameToISOCode.Denmark]: countryConfigDefault,
    [CountryNameToISOCode.Finland]: countryConfigDefault,
    [CountryNameToISOCode.France]: countryConfigDefault,
    [CountryNameToISOCode.Germany]: countryConfigDefault,
    [CountryNameToISOCode.Greece]: countryConfigDefault,
    [CountryNameToISOCode.HongKong]: countryConfigDefault,
    [CountryNameToISOCode.Hungary]: countryConfigDefault,
    [CountryNameToISOCode.India]: countryConfigDefault,
    [CountryNameToISOCode.Indonesia]: countryConfigDefault,
    [CountryNameToISOCode.Ireland]: countryConfigDefault,
    [CountryNameToISOCode.Israel]: countryConfigDefault,
    [CountryNameToISOCode.Italy]: countryConfigDefault,
    [CountryNameToISOCode.Japan]: countryConfigDefault,
    [CountryNameToISOCode.Kenya]: countryConfigDefault,
    [CountryNameToISOCode.Lithuania]: countryConfigDefault,
    [CountryNameToISOCode.Luxembourg]: countryConfigDefault,
    [CountryNameToISOCode.Malaysia]: countryConfigDefault,
    [CountryNameToISOCode.Mexico]: countryConfigDefault,
    [CountryNameToISOCode.Netherlands]: countryConfigDefault,
    [CountryNameToISOCode.NewZealand]: countryConfigDefault,
    [CountryNameToISOCode.Nigeria]: countryConfigDefault,
    [CountryNameToISOCode.Norway]: countryConfigDefault,
    [CountryNameToISOCode.Philippines]: countryConfigDefault,
    [CountryNameToISOCode.Poland]: countryConfigDefault,
    [CountryNameToISOCode.Qatar]: countryConfigDefault,
    [CountryNameToISOCode.RepublicOfKorea]: countryConfigDefault,
    [CountryNameToISOCode.Romania]: countryConfigDefault,
    [CountryNameToISOCode.SaudiArabia]: countryConfigDefault,
    [CountryNameToISOCode.Senegal]: countryConfigDefault,
    [CountryNameToISOCode.Serbia]: countryConfigDefault,
    [CountryNameToISOCode.Singapore]: countryConfigDefault,
    [CountryNameToISOCode.SouthAfrica]: countryConfigDefault,
    [CountryNameToISOCode.Spain]: countryConfigDefault,
    [CountryNameToISOCode.Sweden]: countryConfigDefault,
    [CountryNameToISOCode.Switzerland]: countryConfigDefault,
    [CountryNameToISOCode.Taiwan]: countryConfigDefault,
    [CountryNameToISOCode.Thailand]: countryConfigDefault,
    [CountryNameToISOCode.Türkiye]: countryConfigDefault,
    [CountryNameToISOCode.Ukraine]: countryConfigDefault,
    [CountryNameToISOCode.UnitedArabEmirates]: countryConfigDefault,
    [CountryNameToISOCode.UnitedStatesOfAmerica]: {
      ...countryConfigDefault,
      programOptionFlows: [
        S4C_PROGRAM_OPTION_FLOW.THERAPY,
        S4C_PROGRAM_OPTION_FLOW.SELF_INITIATED_TEENS,
        S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_CHILD,
        S4C_PROGRAM_OPTION_FLOW.PARENT_INITIATED_TEEN,
      ],
    },
    [CountryNameToISOCode.UnitedKingdom]: countryConfigDefault,
    [CountryNameToISOCode.VietNam]: countryConfigDefault,
  },
  expandConditions: [
    {
      target: SHOULD_EXPAND_ACCORDION_CONDITION_TARGETS.WHEN_USER_IS_INTERNATIONAL_AND_EXPANDED_PREFERENCES_DISABLED,
      fn: (condition) => condition === true,
      defaultValueId: MEMBER_PREFERENCE_IDS.HIGHLIGHT_PROVIDERS_WHO_OFFER_IN_PERSON_SESSIONS,
      isCollapseDisabled: true,
    },
  ],
}

export default meetingStylesPreferenceTherapy
